const API_URL =
  import.meta.env.VITE_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:3004`;
// This flag only drives the initial UI state. Authentication is exclusively the
// HttpOnly cookie, which JavaScript cannot read or forge into a valid session.
const AUTH_STATE_KEY = "miboard-authenticated";
let currentUserCache: { token: string; value: UserProfile } | null = null;
let currentUserRequest: Promise<UserProfile> | null = null;
const apiFetch = (input: RequestInfo | URL, init?: RequestInit) =>
  fetch(input, { ...init, credentials: "include" });

export type AuthUser = {
  email: string;
};

export type UserProfile = {
  profile: {
    name: string;
    fullName: string;
    email: string;
    role: "admin" | "member";
    memberSince: string;
    cpf: string;
    phone: string;
    street: string;
    zipCode: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  workspace: {
    id?: string;
    name: string;
    role: string;
    boardsCount: number;
  };
  subscription: {
    planId: string | null;
    planCode: string;
    planName: string;
    planDescription: string;
    status: string;
    billingCycle: string;
    renewalMode: "automatic" | "manual";
    aiAccess: string;
    storage: string;
    seats: string;
    boardLimit: string;
    boardUsage: string;
    workspaceLimit: string;
    support: string;
    nextRenewal: string;
    upgradeHint: string;
    currentPlanPosition: number;
    availablePlans: Array<{
      id: string;
      code: string;
      name: string;
      description: string;
      monthlyPriceCents: number;
      annualPriceCents: number;
      features: string[];
      position: number;
    }>;
  };
};

export const getAuthToken = () => localStorage.getItem(AUTH_STATE_KEY);

export const login = async (email: string, password: string) => {
  const response = await apiFetch(`${API_URL}/api/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    throw new Error("E-mail ou senha inválidos.");
  }
  await response.json();
  localStorage.setItem(AUTH_STATE_KEY, "1");
};
export const signup = async (
  displayName: string,
  email: string,
  password: string,
) => {
  const response = await apiFetch(`${API_URL}/api/auth/signup`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ displayName, email, password }),
  });
  if (!response.ok) throw new Error("Não foi possível criar a conta.");
};

export const requestPasswordReset = async (email: string) => {
  const response = await apiFetch(`${API_URL}/api/auth/forgot-password`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error("Não foi possível enviar o código. Tente novamente.");
  }
};

export const resetPassword = async (
  email: string,
  code: string,
  password: string,
) => {
  const response = await apiFetch(`${API_URL}/api/auth/reset-password`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, code, password }),
  });
  if (!response.ok) {
    throw new Error("Código inválido ou expirado. Solicite um novo código.");
  }
};

export const logout = () => {
  localStorage.removeItem(AUTH_STATE_KEY);
  currentUserCache = null;
  currentUserRequest = null;
  void apiFetch(`${API_URL}/api/auth/logout`, { method: "POST" });
};

export const getCurrentUser = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Usuário não autenticado.");
  }
  if (currentUserCache?.token === token) return currentUserCache.value;
  if (currentUserRequest) return currentUserRequest;
  currentUserRequest = apiFetch(`${API_URL}/api/auth/me`)
    .then(async (response) => {
      if (response.status === 401) {
        logout();
        throw new Error("Sessão expirada. Faça login novamente.");
      }
      if (response.status === 404) {
        logout();
        throw new Error("Usuário não encontrado. Faça login novamente.");
      }
      if (!response.ok) {
        throw new Error("Não foi possível carregar o perfil.");
      }
      const data = (await response.json()) as UserProfile;
      currentUserCache = { token, value: data };
      return data;
    })
    .finally(() => {
      currentUserRequest = null;
    });
  return currentUserRequest;
};
export const updateProfile = async (displayName: string, email: string) => {
  const response = await apiFetch(`${API_URL}/api/auth/me`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ displayName, email }),
  });
  if (!response.ok) throw new Error("Não foi possível salvar o perfil.");
  const data = await response.json();
  currentUserCache = { token: getAuthToken() || "", value: data };
  return data;
};
export const updateMyProfile = async (profile: {
  fullName: string;
  email: string;
  cpf: string;
  phone: string;
  street: string;
  zipCode: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
}) => {
  const response = await apiFetch(`${API_URL}/api/auth/me`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(profile),
  });
  if (!response.ok) throw new Error("Não foi possível salvar o perfil.");
  const data = await response.json();
  currentUserCache = { token: getAuthToken() || "", value: data };
  return data;
};
export const changeMyPassword = async (
  currentPassword: string,
  newPassword: string,
) => {
  const response = await apiFetch(`${API_URL}/api/auth/me/password`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!response.ok) throw new Error("Não foi possível alterar a senha.");
  const result = await response.json();
  logout();
  return result;
};
export const listPlans = async () => {
  const response = await apiFetch(`${API_URL}/api/plans`);
  if (!response.ok) throw new Error("Não foi possível carregar os planos.");
  return response.json() as Promise<{
    plans: UserProfile["subscription"]["availablePlans"];
  }>;
};
export const chooseSubscriptionPlan = async (
  planId: string,
  billingCycle: "monthly" | "annual",
  renewalMode: "automatic" | "manual",
  email?: string,
  customerData?: {
    name?: string;
    cpfCnpj?: string;
    phone?: string;
    postalCode?: string;
    addressNumber?: string;
    street?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    complement?: string;
  },
) => {
  const response = await apiFetch(`${API_URL}/api/auth/me/subscription`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ planId, billingCycle, renewalMode, email, customerData }),
  });
  if (!response.ok) throw new Error("Não foi possível atualizar a assinatura.");
  const data = (await response.json()) as {
    checkout?: { id?: string; link?: string; status?: string };
    order?: unknown;
  };
  currentUserCache = null;
  return data;
};
